import React from "react";
import { XCircle } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../hooks";
import { createProduct } from "../../../slices/productSlice";
import { useStores } from "../../../hooks/useStores";
import DragAndDropUpload from "../../DragAndDropUpload";

interface AddProductModalProps {
  onClose: () => void;
  categories?: string[];
}

export interface ProductFormValues {
  name: string;
  category: string;
  cost_price: string;
  selling_price: string;
  new_category?: string;
  quantity?: string;
  store_id?: string;
  image_url?: string;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ onClose, categories }) => {
  const initialValues: ProductFormValues = {
    name: "",
    category: "",
    cost_price: "",
    selling_price: "",
    new_category: "",
    store_id: "",
    image_url: "",
  };

  const { stores } = useStores();


  const validationSchema = Yup.object({
    name: Yup.string().required("Product name is required"),
    category: Yup.string().required("Category is required"),
    new_category: Yup.string().when("category", {
      is: (value: string) => value === "New Category",
      then: (schema) => schema.required("Please provide the new category"),
      otherwise: (schema) => schema.notRequired(),
    }),
    cost_price: Yup.number()
      .typeError("Must be a number")
      .positive("Must be positive")
      .required("Cost price is required"),
    selling_price: Yup.number()
      .typeError("Must be a number")
      .positive("Must be positive")
      .required("Selling price is required"),
    store_id: Yup.string().required("Please select a store")
  });

  const dispatch = useAppDispatch();

  const handleSubmit = (values: ProductFormValues) => {

    if (values.category == "New Category") {
      values = { ...values, category: values.new_category as string }
    }
    
    const { store_id, ...dispatchArgs } = values;

    const args = {
      products: [dispatchArgs],
      store_id: store_id as string
    }

    dispatch(createProduct(args));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-100 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl p-6 relative shadow-lg text-left">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
        >
          <XCircle color="red" size={20} />
        </button>
        <h2 className="text-lg font-bold mb-4">Add New Product to Store</h2>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form className="space-y-3">
              <div>
                <label className="form-label">
                  Product name
                  <Field
                    type="text"
                    name="name"
                    placeholder="Eg. Canned Milk 500ml"
                    className="w-full border rounded-md px-3 py-2"
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </label>
              </div>

              <div>
                <label className="form-label">
                  Store
                  <Field
                    as="select"
                    name="store_id"
                    className="form-input mt-1 w-full"
                  >
                    <option value="">Select Store</option>
                    {stores?.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="store_id"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </label>
              </div>

              <div>
                <label className="form-label">
                  Category
                  <Field
                    as="select"
                    name="category"
                    className="form-input mt-1 w-full"
                  >
                    <option value="">Select Category</option>
                    {categories?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                    <option value="New Category">New Category</option>
                  </Field>
                  <ErrorMessage
                    name="category"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </label>

                {values.category == "New Category" && (
                  <label className="form-label my-3">
                    Enter new category
                    <Field
                      type="text"
                      name="new_category"
                      placeholder="Eg. Snacks"
                      className="w-full border rounded-md px-3 py-2"
                    />
                    <ErrorMessage
                      name="new_category"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">
                    Cost Price (GHC)
                    <Field
                      type="number"
                      name="cost_price"
                      placeholder="How much does a single unit cost the shop?"
                      className="w-full border rounded-md px-3 py-2"
                    />
                    <ErrorMessage
                      name="cost_price"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </label>
                </div>

                <div>
                  <label className="form-label">
                    Selling Price (GHC)
                    <Field
                      type="number"
                      name="selling_price"
                      placeholder="How much does it cost a customer?"
                      className="w-full border rounded-md px-3 py-2"
                    />
                    <ErrorMessage
                      name="selling_price"
                      component="div"
                      className="text-xs text-red-500 mt-1"
                    />
                  </label>
                </div>
              </div>

              {/* PRODUCT IMAGE */}
              <div>
                <DragAndDropUpload name="image_url" label="Product Image (optional)" />
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border">
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="form-btn-mini"
                >
                  {isSubmitting ? "Saving..." : "Save Product"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddProductModal;
